/**
 * Submit-time validation of the child forms inside form-container controls.
 */

import type { FormControl, FormValidationResult } from '@atlas-forms/types-js';
import { useChildFormRegistryStore, childFormKey } from '@atlas-forms/form-engine-js';
import { nestedControls } from './nestedControls';

type ValidationErrors = FormValidationResult['errors'];

/**
 * Validation errors from the child forms of every form-container in `controls`, including
 * containers inside layouts (card, collapsible panel, tabs, stepper) at any depth.
 *
 * - An invalid child yields one error on its container, so the submit is blocked.
 * - A child whose schema is not known (still loading, failed to load, or its container is
 *   unmounted) cannot be checked, so it also blocks the submit rather than passing as valid.
 */
export function collectChildFormErrors(controls: FormControl[]): ValidationErrors {
  const registry = useChildFormRegistryStore.getState();
  const errors: ValidationErrors = [];

  const visit = (list: FormControl[]) => {
    for (const control of list) {
      if (control.type === 'form-container') {
        checkContainer(control);
      }
      visit(nestedControls(control));
    }
  };

  const checkContainer = (control: FormControl) => {
    const childForms = (control.config?.childForms as Array<{ formID: string; label?: string }> | undefined) ?? [];
    for (const child of childForms) {
      // Keys are scoped to this container (its control id), see childFormKey
      const key = childFormKey(control.id, child.formID);
      const childLabel = child.label || child.formID;
      const childResult = registry.validateChild(key);
      if (!childResult) {
        errors.push({
          field: control.id,
          message: `${childLabel} could not be checked yet. Open it and try again.`,
          code: 'CHILD_FORM_UNAVAILABLE',
          rule: 'childForm',
          value: undefined,
          metadata: { fieldId: control.id, fieldLabel: control.label, childFormID: child.formID },
        } as ValidationErrors[number]);
        continue;
      }
      if (!childResult.isValid) {
        registry.bumpValidation(key);
        errors.push({
          field: control.id,
          message: `${childLabel} has incomplete required fields`,
          code: 'CHILD_FORM_INVALID',
          rule: 'childForm',
          value: undefined,
          metadata: {
            fieldId: control.id,
            fieldLabel: control.label,
            childFormID: child.formID,
            childErrorCount: childResult.errors.length,
          },
        } as ValidationErrors[number]);
      }
    }
  };

  visit(controls);
  return errors;
}
